import { create } from "zustand"

import {
  claimCallLock,
  otherTabOnCall,
  useOtherTabOnCall
} from "~/utils/call-lock"

type VoicePerson = {
  id: string
  name: string
  title?: string
  phone?: string
  contact_phone?: string
  account?: { id: string; name: string }
  // Local-presence number matching and the calling-hours window both read these.
  tz?: string
  location_country?: string
  linkedin_url?: string
  phone_lookup?: "not_found"
  phone_lookup_reason?: "not_found" | "excluded" | "quota"
  phone_lookup_detail?: string
  listId?: string
}

// An answered callback opens the same in-call panel a placed call does, but the call already
// exists, so the accepted handle travels with the person.
type VoiceInbound = { person: VoicePerson; call: unknown }

// Dialer options, set from the rail and read at dial time.
//
// callerNumber is the number to present as caller ID. NULL IS NOT "unset" — it
// is Boost Connect, the automatic choice, and it is the default. dialer-service
// already picks a number matching the callee's country when reserve-call omits
// outbound_number, so Boost Connect is that existing behaviour named, and a
// concrete number is the rep overriding it. Nothing here changes how a call is
// placed when the rep leaves it alone.
// Boost Connect's settings: country-code matching plus rotation.
//
// These reach the backend as params on the voice-answer webhook
// (calleeCountry / rotateAfter) — there is no column for them, so this store is
// the source of truth and it mirrors to localStorage. That is why the rail's
// modal and the bulk dialer must read the SAME object: they were separate
// component state before, so the dialer forgot the settings every time it
// opened and the two disagreed about what the next call would do.
export type CallerConfig = {
  rotation: boolean
  rotateN: number
  countryMatch: boolean
}

const CALLER_CONFIG_KEY = "os.dialer.callerConfig"
const CALLER_CONFIG_DEFAULT: CallerConfig = {
  rotation: true,
  // 20 is the recommended rotation threshold.
  rotateN: 20,
  countryMatch: true
}

function readCallerConfig(): CallerConfig {
  try {
    const raw = localStorage.getItem(CALLER_CONFIG_KEY)
    if (!raw) return CALLER_CONFIG_DEFAULT
    const p = JSON.parse(raw) as Partial<CallerConfig>
    return {
      rotation: typeof p.rotation === "boolean" ? p.rotation : true,
      rotateN: Number.isFinite(Number(p.rotateN))
        ? Math.min(50, Math.max(10, Number(p.rotateN)))
        : 20,
      countryMatch: typeof p.countryMatch === "boolean" ? p.countryMatch : true
    }
  } catch {
    // Private windows and blocked site data both throw here.
    return CALLER_CONFIG_DEFAULT
  }
}

type CallerNumber = string | null
type CallingHours = { start: string | null; end: string | null } | null

export const completeCallingHours = (h: CallingHours) =>
  h?.start && h?.end ? { start: h.start, end: h.end } : undefined

export const DEFAULT_CALLING_HOURS = { start: "09:00", end: "17:00" }

export const MAX_PARALLEL_LINES = 5

type VoiceSessionStore = {
  callerNumber: CallerNumber
  setCallerNumber: (n: CallerNumber) => void
  callerConfig: CallerConfig
  setCallerConfig: (c: Partial<CallerConfig>) => void
  callingHours: CallingHours
  setCallingHours: (h: CallingHours) => void
  parallelLines: number
  setParallelLines: (n: number) => void
  autoAdvance: boolean
  setAutoAdvance: (v: boolean) => void
  bulkPeople: VoicePerson[] | null
  single: VoicePerson | null
  inbound: VoiceInbound | null
  startBulk: (people: VoicePerson[]) => void
  startSingle: (person: VoicePerson) => void
  startInbound: (v: VoiceInbound) => void
  closeBulk: () => void
  closeSingle: () => void
  closeInbound: () => void
}

const useVoiceSessionStore = create<VoiceSessionStore>((set) => ({
  callerNumber: null,
  setCallerNumber: (callerNumber) => set({ callerNumber }),
  callerConfig: readCallerConfig(),
  setCallerConfig: (patch) =>
    set((s) => {
      const next = { ...s.callerConfig, ...patch }
      try {
        localStorage.setItem(CALLER_CONFIG_KEY, JSON.stringify(next))
      } catch {
        // Not being able to remember the choice is not a reason to reject it.
      }
      return { callerConfig: next }
    }),
  autoAdvance: false,
  setAutoAdvance: (autoAdvance) => set({ autoAdvance }),
  parallelLines: 1,
  setParallelLines: (parallelLines) =>
    set({
      parallelLines: Math.min(Math.max(parallelLines, 1), MAX_PARALLEL_LINES)
    }),
  callingHours: { ...DEFAULT_CALLING_HOURS },
  setCallingHours: (callingHours) =>
    set({
      callingHours:
        callingHours?.start &&
        callingHours?.end &&
        callingHours.start >= callingHours.end
          ? { ...callingHours, end: null }
          : callingHours
    }),
  bulkPeople: null,
  single: null,
  inbound: null,
  // One dialler at a time. A rep who starts a session and then clicks Call on a
  // row used to get both windows stacked, each with its own idea of the call.
  // The lock check is a backstop behind the disabled controls: claiming it here
  // rather than waiting for the holder's heartbeat closes the window where two
  // tabs clicking at once both believe they are first.
  startBulk: (people) => {
    if (otherTabOnCall()) return
    claimCallLock()
    set({ bulkPeople: people, single: null })
  },
  startSingle: (person) => {
    if (otherTabOnCall()) return
    claimCallLock()
    set({ single: person, bulkPeople: null })
  },
  startInbound: (v) => set({ inbound: v }),
  closeBulk: () => set({ bulkPeople: null }),
  closeSingle: () => set({ single: null }),
  closeInbound: () => set({ inbound: null })
}))

// One live call at a time (ENG-3391). Every surface that can start a call reads this
// rather than deciding for itself, so a running session greys out the other triggers
// instead of silently replacing the panel the rep is already talking through.
export function useCallSessionActive() {
  return useVoiceSessionStore((s) =>
    Boolean(s.bulkPeople || s.single || s.inbound)
  )
}

// What dial controls gate on: a session in this tab, or one in any other tab of
// the same browser.
export function useCallBlocked() {
  // Both read unconditionally — `a() || b()` would short-circuit past a hook and
  // change the hook order between renders.
  const localSession = useCallSessionActive()
  const otherTab = useOtherTabOnCall()
  return localSession || otherTab
}

export default useVoiceSessionStore
